/**
 * Payment Store
 * Manages payment flow state using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BalanceResponse, PreparePaymentResponse } from '@/types/api.types';
import { SESSION_KEYS } from '@/config/app.config';

interface PaymentState {
  // State
  balance: BalanceResponse | null;
  paymentAmount: number | null;
  cybersourceData: PreparePaymentResponse | null;

  // Actions
  setBalance: (balance: BalanceResponse) => void;
  setPaymentAmount: (amount: number) => void;
  setCybersourceData: (data: PreparePaymentResponse) => void;
  clearPaymentData: () => void;
}

export const usePaymentStore = create<PaymentState>()(
  persist(
    (set) => ({
      // Initial state
      balance: null,
      paymentAmount: null,
      cybersourceData: null,

      // Set balance information
      setBalance: (balance: BalanceResponse) => {
        set({ balance });
      },

      // Set payment amount
      setPaymentAmount: (amount: number) => {
        set({ paymentAmount: amount });
      },

      // Set Cybersource form data
      setCybersourceData: (data: PreparePaymentResponse) => {
        set({ cybersourceData: data });
      },

      // Clear all payment data
      clearPaymentData: () => {
        set({
          balance: null,
          paymentAmount: null,
          cybersourceData: null,
        });
      },
    }),
    {
      name: SESSION_KEYS.PAYMENT_AMOUNT, // Storage key
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    }
  )
);
